import type { ReactNode } from "react";

/**
 * Tooltip / accessible name for {@link IncompleteProfileMarker}. Exported so
 * tests (and any copy-sensitive surface) pin the exact string in one place.
 */
export const INCOMPLETE_PROFILE_TOOLTIP = "This organization's profile is incomplete";

interface IncompleteProfileMarkerProps {
  /** Square glyph size in px. */
  size?: number;
  /** Extra classes for layout/spacing at the call site (never for recolouring). */
  className?: string;
}

/**
 * Warning marker for an enrollment whose referring partner org has an incomplete profile.
 *
 * Every enrollment list and detail view that flags `referrerProfileIncomplete`
 * renders it, so the glyph and tooltip stay identical. Callers gate it on the
 * wire flag; nothing is cached client-side.
 */
export function IncompleteProfileMarker({
  size = 14,
  className,
}: IncompleteProfileMarkerProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={INCOMPLETE_PROFILE_TOOLTIP}
      className={className ? `shrink-0 ${className}` : "shrink-0"}
    >
      {/* Native hover tooltip. */}
      <title>{INCOMPLETE_PROFILE_TOOLTIP}</title>
      <path
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        className="fill-amber-400"
        stroke="none"
      />
      <line x1="12" y1="9.5" x2="12" y2="14" stroke="#000" strokeWidth={2} strokeLinecap="round" />
      <line
        x1="12"
        y1="17.5"
        x2="12.01"
        y2="17.5"
        stroke="#000"
        strokeWidth={2.4}
        strokeLinecap="round"
      />
    </svg>
  );
}
